import React, { useEffect, useMemo, useRef, useCallback } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { StakeholderColumn, useGetStakeholderRelation } from '../dataHooks';
import { MOCKED_EMPLOYEE_OPTIONS_COMPENSATION } from './MultiStepMenu';
import { StakeholderErrorState, StakeholderEmptyState, HierarchyLevelCell } from './shared/stakeholderComponents';
import {
  STAKEHOLDER_OPTIONS_PROPOSER,
  STAKEHOLDER_OPTIONS_APPROVER,
  STAKEHOLDER_OPTIONS_PUBLISHER,
  MOCK_HIERARCHICAL_ROW_DATA,
} from './shared/stakeholderConstants';
// Shared imports
import { StakeHolderTableProps } from './shared/stakeholderTypes';
import { createMultiSelectMenuRendererTreeTable } from './shared/stakeholderUtils';
import {
  Employee,
  StakeholderTreeRow,
  setModifiedStakeholdersTreeRows,
  setOriginalStakeholdersTreeRows,
  useCompensationDrawersState,
} from './slice/compensationDrawersSlice';

// Stable empty arrays to prevent unnecessary re-renders
const EMPTY_COLUMNS: StakeholderColumn[] = [];
const EMPTY_ROWS: StakeholderTreeRow[] = [];

const MOCK_HIERARCHICAL_ROW_DATA_STAKEHOLDERS = MOCK_HIERARCHICAL_ROW_DATA.map((row, index) => ({
  ...row,
  PROPOSER: STAKEHOLDER_OPTIONS_PROPOSER.MANAGER.value,
  APPROVER: STAKEHOLDER_OPTIONS_APPROVER.SKIP_MANAGER.value,
  PUBLISHER: STAKEHOLDER_OPTIONS_PUBLISHER.ANY_EMPLOYEE.value,
  publisherSelectedEmployees: row.publisherSelectedEmployees ?? [MOCKED_EMPLOYEE_OPTIONS_COMPENSATION[index]],
  HIERARCHY: row.hierarchy,
}));

const MOCK_STAKEHOLDER_TREE_COLUMNS = [
  {
    columnId: 'PROPOSER',
  },
  {
    columnId: 'APPROVER',
  },
  {
    columnId: 'PUBLISHER',
  },
];

const additionalColumnsPropsMap = {
  function: {
    flex: 1,
    autoHeight: true,
    metadata: { isEmployeeSubtitle: true },
    headerName: 'Function tree',
  },
  PROPOSER: {
    headerName: 'Proposer',
    cellRenderer: createMultiSelectMenuRendererTreeTable(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_PROPOSER,
    },
    minWidth: 240,
  },
  APPROVER: {
    headerName: 'Approver',
    cellRenderer: createMultiSelectMenuRendererTreeTable(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_APPROVER,
    },
    minWidth: 240,
  },
  PUBLISHER: {
    headerName: 'Publisher',
    cellRenderer: createMultiSelectMenuRendererTreeTable(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_PUBLISHER,
    },
    minWidth: 240,
  },
};

export const mapEmployeeForMultiStepMenu = (employee: Employee) => ({
  profilePhoto: employee.profilePhoto,
  label: employee.displayName,
  value: employee.id,
  id: employee.id,
});

function StakeHolderTreeTable({ gridRef, gridStatePersistenceKey }: StakeHolderTableProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const { modifiedStakeholdersTreeRows } = useCompensationDrawersState();
  const dispatch = useDispatch();

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: 'Function tree',
      minWidth: 220,
      columnId: 'function',
      displayName: 'Function tree',
      lockPosition: 'left' as const,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: HierarchyLevelCell,
      },
      cellStyle: { 'padding-left': '0px' },
    }),
    []
  );

  const {
    data: stakeholderRelationData,
    isLoading,
    error,
  } = useGetStakeholderRelation({
    compensationId: 1, // TODO : make it dynamic
  });

  const columns = useMemo(() => stakeholderRelationData?.columns ?? EMPTY_COLUMNS, [stakeholderRelationData?.columns]);
  const rows = useMemo(() => stakeholderRelationData?.rows ?? EMPTY_ROWS, [stakeholderRelationData?.rows]);

  const columnDefs = useMemo(
    () =>
      checkStaticDataCompensationEnabled()
        ? MOCK_STAKEHOLDER_TREE_COLUMNS.map((column) => ({
            ...column,
            ...(additionalColumnsPropsMap[column.columnId] || {}),
          }))
        : columns
            .map((column) => ({
              ...column,
              ...(additionalColumnsPropsMap[column.columnId] || {}),
            }))
            .filter((column) => MOCK_STAKEHOLDER_TREE_COLUMNS.some((c) => c.columnId === column.columnId)),
    [columns]
  );

  const formattedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        function: { displayName: row.FUNCTION },
        PROPOSER: row?.PROPOSER?.stakeholderRelation ?? 'ANY_EMPLOYEE',
        APPROVER: row?.APPROVER?.stakeholderRelation ?? 'ANY_EMPLOYEE',
        PUBLISHER: row?.PUBLISHER?.stakeholderRelation ?? 'ANY_EMPLOYEE',
        proposerSelectedEmployees:
          row?.PROPOSER?.employees?.length > 0 ? row?.PROPOSER?.employees?.map(mapEmployeeForMultiStepMenu) : null,
        approverSelectedEmployees:
          row?.APPROVER?.employees?.length > 0 ? row?.APPROVER?.employees?.map(mapEmployeeForMultiStepMenu) : null,
        publisherSelectedEmployees:
          row?.PUBLISHER?.employees?.length > 0 ? row?.PUBLISHER?.employees?.map(mapEmployeeForMultiStepMenu) : null,
        id: row.ID,
      })),
    [rows]
  );

  const updateStakeholderRows = useCallback(
    (newRows: any[]) => {
      dispatch(setModifiedStakeholdersTreeRows(newRows));
      dispatch(setOriginalStakeholdersTreeRows(newRows));
    },
    [dispatch]
  );

  useEffect(() => {
    if (checkStaticDataCompensationEnabled()) {
      updateStakeholderRows(MOCK_HIERARCHICAL_ROW_DATA_STAKEHOLDERS);
    } else {
      updateStakeholderRows(formattedRows);
    }
  }, [formattedRows, updateStakeholderRows]);

  if (error) {
    return (
      <Box display="flex" alignItems="flex-start" borderRadius="8px" width="100%">
        <Box
          className="stakeholder-tree-list-table"
          width="100%"
          height="calc(100vh - 455px)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <StakeholderErrorState
            heading="No stake holders are available!"
            description="There was an issue fetching the stake holders. Please try again later."
          />
        </Box>
      </Box>
    );
  }

  if (modifiedStakeholdersTreeRows?.length === 0 && !isLoading) {
    return (
      <Box display="flex" alignItems="flex-start" borderRadius="8px" width="100%">
        <Box
          className="stakeholder-flat-list-table"
          width="100%"
          height="calc(100vh - 455px)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <StakeholderEmptyState heading="No stake holders are available!" />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" borderRadius="8px" width="100%">
      <Box className="stakeholder-flat-list-table" width="100%" height="calc(100vh - 265px)">
        <AgGridTable
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={{
            headerComponent: CustomHeaderCell,
          }}
          paginationAutoPageSize={false}
          headerHeight={40}
          rowHeight={66}
          sideBar={null}
          showSidebarToggle={false}
          loading={checkStaticDataCompensationEnabled() ? false : isLoading}
          rowData={modifiedStakeholdersTreeRows}
          getRowId={(params) => params.data.id}
          treeData
          animateRows
          groupDefaultExpanded={-1}
          getDataPath={(data) => data.HIERARCHY}
          autoGroupColumnDef={autoGroupColumnDef}
          kebabMenuIsRowSpecific={false}
          getRowStyle={() => ({ border: '0px' })}
        />
      </Box>
    </Box>
  );
}

export { StakeHolderTreeTable };
